import { Tooltip } from '@highlight/ui'
import TimeAgo from 'react-timeago'

type Unit = 'second' | 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year'

export function makeSmartFormatter(opts?: {
  timeZone?: string
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6 // 0=Sun
}) {
  const timeZone = opts?.timeZone
  const weekStartsOn = opts?.weekStartsOn ?? 0

  const MS = { minute: 60_000, hour: 3_600_000, day: 86_400_000 }
  const pad2 = (n: number) => n.toString().padStart(2, '0')

  const clock = (d: Date) => {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZone,
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    }).formatToParts(d)
    let h = '',
      m = '',
      ap = ''
    for (const p of parts) {
      if (p.type === 'hour') h = p.value
      if (p.type === 'minute') m = p.value
      if (p.type === 'dayPeriod') ap = p.value.toUpperCase()
    }
    return `${h}:${pad2(Number(m))} ${ap}`.trim()
  }

  const weekdayLong = (d: Date) => new Intl.DateTimeFormat(undefined, { timeZone, weekday: 'long' }).format(d)

  const weekdayIndex = (d: Date) => {
    const short = new Intl.DateTimeFormat(undefined, { timeZone, weekday: 'short' }).format(d)
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(short)
  }

  const absoluteWithWeekday = (d: Date) => {
    const date = new Intl.DateTimeFormat(undefined, {
      timeZone,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(d)
    return `${weekdayLong(d)}, ${date}, ${clock(d)}`
  }

  const toMs = (value: number, unit: Unit) => {
    switch (unit) {
      case 'second':
        return value * 1_000
      case 'minute':
        return value * MS.minute
      case 'hour':
        return value * MS.hour
      case 'day':
        return value * MS.day
      case 'week':
        return value * 7 * MS.day
      case 'month':
        return value * 30 * MS.day
      case 'year':
        return value * 365 * MS.day
    }
  }

  const startOfLocalDayUTC = (d: Date) => {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    })
      .formatToParts(d)
      .reduce<Record<string, string>>((a, p) => {
        if (p.type !== 'literal') a[p.type] = p.value
        return a
      }, {})
    return Date.UTC(+parts.year, +parts.month - 1, +parts.day)
  }

  const weekInfo = (date: Date, now: Date) => {
    const dateDay = startOfLocalDayUTC(date)
    const nowDay = startOfLocalDayUTC(now)
    const dateW = weekdayIndex(date)
    const nowW = weekdayIndex(now)
    const backDate = (7 + (dateW - weekStartsOn)) % 7
    const backNow = (7 + (nowW - weekStartsOn)) % 7
    const dateWeekStart = dateDay - backDate * MS.day
    const nowWeekStart = nowDay - backNow * MS.day
    return {
      same: dateWeekStart === nowWeekStart,
      last: dateWeekStart === nowWeekStart - 7 * MS.day,
      dayDiff: Math.round((nowDay - dateDay) / MS.day),
    }
  }

  // Correct 6-arg signature for react-timeago
  return function smartFormatter(
    value: number,
    unit: Unit,
    suffix: 'ago' | 'from now',
    _epochMilliseconds: number,
    _nextFormatter: any,
    nowFn: () => number,
  ): string {
    const nowMs = nowFn()
    const deltaMs = toMs(value, unit)
    const dateMs = suffix === 'ago' ? nowMs - deltaMs : nowMs + deltaMs
    const date = new Date(dateMs)
    const now = new Date(nowMs)

    // Under 24h → relative
    if (suffix === 'ago' && deltaMs < MS.day) {
      if (unit === 'second') return value <= 5 ? 'Just now' : `${value} seconds ago`
      if (unit === 'minute') return `${value} minute${value === 1 ? '' : 's'} ago`
      if (unit === 'hour') return `${value} hour${value === 1 ? '' : 's'} ago`
      return 'Just now'
    }

    // Calendar rules
    const { same, last, dayDiff } = weekInfo(date, now)

    if (suffix === 'ago' && dayDiff === 1) {
      return `Yesterday, ${clock(date)}`
    }
    if (suffix === 'ago' && same) {
      return `${weekdayLong(date)}, ${clock(date)}`
    }
    if (suffix === 'ago' && last) {
      return `Last ${weekdayLong(date)}, ${clock(date)}`
    }

    // Future times: short relative under 24h, else absolute with weekday
    if (suffix === 'from now') {
      if (deltaMs < MS.minute) return 'in moments'
      if (deltaMs < MS.hour) return `in ${Math.round(deltaMs / MS.minute)} minutes`
      if (deltaMs < MS.day) return `in ${Math.round(deltaMs / MS.hour)} hours`
      return absoluteWithWeekday(date)
    }

    // Older than last week → absolute with weekday (your new rule)
    return absoluteWithWeekday(date)
  }
}

const formatter = makeSmartFormatter()

export const RelativeTimeAgo = ({ date }: { date: Date | number | string }) => {
  return (
    <Tooltip>
      <Tooltip.Trigger render={<span className="font-inherit inline-flex text-inherit" />}>
        <TimeAgo date={date ?? 0} minPeriod={60} formatter={formatter} title={''} />
      </Tooltip.Trigger>
      <Tooltip.Content sideOffset={4}>{new Date(date).toLocaleString()}</Tooltip.Content>
    </Tooltip>
  )
}
